import { z } from 'zod';
import { emailSchema, normalizeEmail } from './accounts.js';
import { localDateSchema, localTimeSchema } from './editions.js';

/**
 * PRD 16 M7 Komunikacja (etap E2): log wysyłek (US-707), treści edycji (US-106), harmonogram przypomnień (US-107, US-704)
 * i kampanie (US-705). Czasy jako ISO 8601; daty i godziny z formularzy – lokalne (Europe/Warsaw).
 */

// --- Log wysyłek (US-707) ---

export const emailCategories = ['transactional', 'plan', 'reminder', 'campaign'] as const;
export type EmailCategoryCode = (typeof emailCategories)[number];

/** AC-1: „ponawiany” to mail w kolejce po nieudanej próbie. */
export const mailLogStatuses = ['queued', 'retrying', 'sending', 'sent', 'failed', 'bounced'] as const;
export type MailLogStatus = (typeof mailLogStatuses)[number];

const emptyToUndefined = (value: unknown) => (value === '' ? undefined : value);

/** AC-2: filtry logu (parametry adresu – wszystkie opcjonalne). */
export const mailLogQuerySchema = z.object({
  email: z.preprocess(emptyToUndefined, z.string().trim().max(200).optional()),
  category: z.preprocess(emptyToUndefined, z.enum(emailCategories).optional()),
  status: z.preprocess(emptyToUndefined, z.enum(mailLogStatuses).optional()),
  editionId: z.preprocess(emptyToUndefined, z.string().uuid().optional()),
  campaignId: z.preprocess(emptyToUndefined, z.string().uuid().optional()),
  from: z.preprocess(emptyToUndefined, localDateSchema.optional()),
  to: z.preprocess(emptyToUndefined, localDateSchema.optional()),
  page: z.preprocess(emptyToUndefined, z.coerce.number().int().min(1).default(1)),
});
export type MailLogQuery = z.infer<typeof mailLogQuerySchema>;

export const mailLogEntrySchema = z.object({
  id: z.string().uuid(),
  to: z.string(),
  accountId: z.string().uuid().nullable(),
  category: z.enum(emailCategories),
  template: z.string(),
  subject: z.string(),
  status: z.enum(mailLogStatuses),
  attempts: z.number().int(),
  createdAt: z.string(),
  sentAt: z.string().nullable(),
  lastError: z.string().nullable(),
  edition: z.object({ id: z.string().uuid(), name: z.string() }).nullable(),
  campaign: z.object({ id: z.string().uuid(), name: z.string() }).nullable(),
});
export type MailLogEntry = z.infer<typeof mailLogEntrySchema>;

export const mailLogPageSchema = z.object({
  entries: z.array(mailLogEntrySchema),
  total: z.number().int(),
  page: z.number().int(),
  pageSize: z.number().int(),
});
export type MailLogPage = z.infer<typeof mailLogPageSchema>;

export const mailDetailSchema = mailLogEntrySchema.extend({ html: z.string(), text: z.string() });
export type MailDetail = z.infer<typeof mailDetailSchema>;

/** US-707 AC-4: ostatnie maile konta w widoku obsługi. */
export const accountMailListSchema = z.object({ entries: z.array(mailLogEntrySchema) });

/** Postęp wysyłki przypomnienia albo kampanii (US-704 AC-3, US-705 AC-5). */
export const sendProgressSchema = z.object({
  sent: z.number().int(),
  queued: z.number().int(),
  failed: z.number().int(),
});
export type SendProgress = z.infer<typeof sendProgressSchema>;

/** Podgląd maila (US-106 AC-3, US-704 AC-4, US-705 AC-4). Obrazki wbudowane zamienione na `data:` – do ramki w panelu. */
export const mailPreviewSchema = z.object({
  /** Konto, dla którego zbudowano treść. */
  recipient: z.string(),
  subject: z.string(),
  html: z.string(),
  text: z.string(),
});
export type MailPreview = z.infer<typeof mailPreviewSchema>;

export const testMailResultSchema = z.object({ sentTo: z.string() });
export type TestMailResult = z.infer<typeof testMailResultSchema>;

// --- Treści edycji (US-106) ---

const richText = (max: number) => z.string().max(max, { message: `Maksymalnie ${max} znaków.` });

export const editionContentSchema = z.object({
  /** Dojazd, wejście, co zabrać – strona edycji i przypomnienie. */
  infoHtml: richText(20_000),
  /** Zastrzeżenia, np. o fotografowaniu. */
  noticeHtml: richText(10_000),
});
export type EditionContent = z.infer<typeof editionContentSchema>;

/** Stopka wszystkich maili – jedna, niezależna od edycji (PRD §10 pkt 36); pusta = bez stopki. */
export const mailFooterSchema = z.object({ html: richText(5_000) });
export type MailFooterInput = z.infer<typeof mailFooterSchema>;
export const mailFooterResponseSchema = z.object({ footer: z.string().nullable() });
export type MailFooterResponse = z.infer<typeof mailFooterResponseSchema>;

// --- Harmonogram przypomnień (US-107) i przypomnienia (US-704) ---

export const reminderInputSchema = z.object({ date: localDateSchema, time: localTimeSchema });
export type ReminderInput = z.infer<typeof reminderInputSchema>;

/** `pending` – czeka; `sending` – maile trafiają do kolejki; `sent` – wszystkie w kolejce/wysłane; `skipped` – nie wysłano (AC-4). */
export const reminderStates = ['pending', 'sending', 'sent', 'skipped'] as const;
export type ReminderState = (typeof reminderStates)[number];

export const reminderSchema = z.object({
  id: z.string().uuid(),
  sendAt: z.string(),
  date: z.string(),
  time: z.string(),
  state: z.enum(reminderStates),
  /** Dlaczego pominięto (np. edycja nieopublikowana w chwili terminu). */
  skippedReason: z.string().nullable(),
  progress: sendProgressSchema,
});
export type Reminder = z.infer<typeof reminderSchema>;
export const reminderListSchema = z.object({ reminders: z.array(reminderSchema) });

/** US-704 AC-4: podgląd / próba dla wybranego konta (e‑mail); bez niego – pierwsze zgłoszone konto. */
export const sampleRecipientSchema = z.object({
  email: z.preprocess(emptyToUndefined, z.string().trim().max(200).optional()),
});
export type SampleRecipient = z.infer<typeof sampleRecipientSchema>;

// --- Kampanie (US-705) ---

export const campaignKinds = ['organizational', 'marketing'] as const;
export type CampaignKind = (typeof campaignKinds)[number];

/** Wstawki w temacie i treści (AC-1). */
export const CAMPAIGN_PLACEHOLDERS = {
  firstName: '{imię}',
  editionName: '{nazwa edycji}',
  planLink: '{link do planu}',
} as const;

/**
 * AC-2: kryteria odbiorców. Konto spełniające **którekolwiek** kryterium jest odbiorcą (suma zbiorów, bez powtórzeń).
 * „Odprawione w edycji X” = konto ma w tej edycji co najmniej jedną osobę odprawioną na wejściu (M6), np. ankieta po wydarzeniu.
 * „Prowadzący w edycji X” = konto przypisane do nieodwołanych zajęć edycji (PRD §10 pkt 47) – także bez ustawionego hasła.
 * „Adresy wpisane ręcznie” (PRD §10 pkt 59) – także bez konta w systemie; zapisywane małymi literami, bez powtórzeń.
 */
export const CAMPAIGN_MAX_MANUAL_EMAILS = 1000;
export const campaignCriteriaSchema = z
  .object({
    registeredEditionIds: z.array(z.string().uuid()).max(50).default([]),
    activityIds: z.array(z.string().uuid()).max(500).default([]),
    checkedInEditionIds: z.array(z.string().uuid()).max(50).default([]),
    speakerEditionIds: z.array(z.string().uuid()).max(50).default([]),
    emails: z
      .array(emailSchema)
      .max(CAMPAIGN_MAX_MANUAL_EMAILS, { message: `Najwyżej ${CAMPAIGN_MAX_MANUAL_EMAILS} adresów.` })
      .transform((list) => [...new Set(list.map(normalizeEmail))])
      .default([]),
    newsletter: z.boolean().default(false),
  })
  .refine(
    (c) =>
      c.registeredEditionIds.length > 0 ||
      c.activityIds.length > 0 ||
      c.checkedInEditionIds.length > 0 ||
      c.speakerEditionIds.length > 0 ||
      c.emails.length > 0 ||
      c.newsletter,
    {
      message: 'Wybierz co najmniej jedno kryterium odbiorców.',
      path: ['registeredEditionIds'],
    },
  );
export type CampaignCriteria = z.infer<typeof campaignCriteriaSchema>;

const campaignText = (max: number) =>
  z.string().trim().min(1, { message: 'To pole jest wymagane.' }).max(max, { message: `Maksymalnie ${max} znaków.` });

export const campaignInputSchema = z
  .object({
    name: campaignText(120),
    kind: z.enum(campaignKinds),
    /** Edycja wstawek `{nazwa edycji}` i `{link do planu}` oraz jej stopka maila. */
    editionId: z.string().uuid().nullable(),
    subject: campaignText(200),
    bodyHtml: campaignText(50_000),
    criteria: campaignCriteriaSchema,
  })
  .refine(
    (c) =>
      c.editionId !== null ||
      ![c.subject, c.bodyHtml].some(
        (text) => text.includes(CAMPAIGN_PLACEHOLDERS.editionName) || text.includes(CAMPAIGN_PLACEHOLDERS.planLink),
      ),
    { message: 'Wstawki {nazwa edycji} i {link do planu} wymagają wybrania edycji.', path: ['editionId'] },
  );
export type CampaignInput = z.infer<typeof campaignInputSchema>;

/** `sent` – wszystkie maile kampanii wyszły albo skończyły się błędem. */
export const campaignStatuses = ['draft', 'scheduled', 'sending', 'paused', 'sent'] as const;
export type CampaignStatus = (typeof campaignStatuses)[number];

export const campaignSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  kind: z.enum(campaignKinds),
  editionId: z.string().uuid().nullable(),
  subject: z.string(),
  bodyHtml: z.string(),
  criteria: z.object({
    registeredEditionIds: z.array(z.string().uuid()),
    activityIds: z.array(z.string().uuid()),
    checkedInEditionIds: z.array(z.string().uuid()),
    speakerEditionIds: z.array(z.string().uuid()),
    emails: z.array(z.string()),
    newsletter: z.boolean(),
  }),
  status: z.enum(campaignStatuses),
  scheduledAt: z.string().nullable(),
  /** Ustalone w chwili startu wysyłki. */
  recipients: z.number().int().nullable(),
  excludedNoConsent: z.number().int().nullable(),
  progress: sendProgressSchema,
  createdBy: z.string().nullable(),
  createdAt: z.string(),
  startedBy: z.string().nullable(),
  startedAt: z.string().nullable(),
});
export type Campaign = z.infer<typeof campaignSchema>;
export const campaignListSchema = z.object({ campaigns: z.array(campaignSchema) });

/** AC-3, AC-4: liczba odbiorców i ilu odfiltrowano (kampania marketingowa bez zgody na newsletter). */
export const campaignAudienceRequestSchema = z.object({
  kind: z.enum(campaignKinds),
  criteria: campaignCriteriaSchema,
});
export type CampaignAudienceRequest = z.infer<typeof campaignAudienceRequestSchema>;
export const campaignAudienceSchema = z.object({
  recipients: z.number().int(),
  excludedNoConsent: z.number().int(),
});
export type CampaignAudience = z.infer<typeof campaignAudienceSchema>;

/** AC-5: bez daty – od razu; z datą i godziną (lokalne) – o zaplanowanym czasie. */
export const campaignSendSchema = z.object({
  date: localDateSchema.optional(),
  time: localTimeSchema.optional(),
});
export type CampaignSend = z.infer<typeof campaignSendSchema>;

/** AC-6: szablony kampanii w bazie. */
export const campaignTemplateInputSchema = z.object({
  name: campaignText(120),
  kind: z.enum(campaignKinds),
  subject: campaignText(200),
  bodyHtml: campaignText(50_000),
});
export type CampaignTemplateInput = z.infer<typeof campaignTemplateInputSchema>;
export const campaignTemplateSchema = campaignTemplateInputSchema.extend({
  id: z.string().uuid(),
  createdAt: z.string(),
});
export type CampaignTemplate = z.infer<typeof campaignTemplateSchema>;
export const campaignTemplateListSchema = z.object({ templates: z.array(campaignTemplateSchema) });

// --- Newsletter (US-705 AC-3) ---

export const newsletterUnsubscribeResultSchema = z.object({
  /** `unsubscribed` – zgoda wycofana teraz; `already` – zgody już nie było. */
  result: z.enum(['unsubscribed', 'already']),
});
export type NewsletterUnsubscribeResult = z.infer<typeof newsletterUnsubscribeResultSchema>;
